import { useState, useEffect } from "react";
import { X } from "lucide-react";

export function SaveProjectModal({ open, onOpenChange, onSave, onEdit, currentProject }) {
  const [projectName, setProjectName] = useState("");
  const [productionOrder, setProductionOrder] = useState("");
  const [designer, setDesigner] = useState(localStorage.getItem("user_name") || "");
  const [filePath, setFilePath] = useState("");
  const [touched, setTouched] = useState(false);

  const isEditing = !!currentProject;
  const nameError = touched && projectName.trim() === "";

  useEffect(() => {
    if (open && currentProject) {
      setProjectName(currentProject.name || "");
      setProductionOrder(currentProject.productionOrder || currentProject.production_order || "");
      setDesigner(currentProject.designer || localStorage.getItem("user_name") || "");
      setFilePath(currentProject.file_path || currentProject.filePath || "");
    } else if (open) {
      setProjectName("");
      setProductionOrder("");
      setDesigner(localStorage.getItem("user_name") || "");
      setFilePath("");
    }
    setTouched(false);
  }, [open, currentProject]);

  const reset = () => {
    setProjectName("");
    setProductionOrder("");
    setDesigner(localStorage.getItem("user_name") || "");
    setFilePath("");
    setTouched(false);
  };

  const handleSaveNew = () => {
    setTouched(true);
    if (!projectName.trim()) return;

    if (onSave) {
      onSave({ projectName, productionOrder, designer, filePath });
    }
    reset();
    onOpenChange(false);
  };

  const handleEdit = () => {
    setTouched(true);
    if (!projectName.trim()) return;

    if (onEdit) {
      onEdit({ projectName, productionOrder, designer, filePath, id: currentProject.id });
    }
    reset();
    onOpenChange(false);
  };

  const handleCancel = () => {
    reset();
    onOpenChange(false);
  };

  if (!open) return null;

  const inputStyle = {
    width: "100%",
    backgroundColor: "#0d0d1a",
    border: "1px solid #2a2a4a",
    borderRadius: "6px",
    padding: "9px 12px",
    color: "#fff",
    fontSize: "13px",
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.2s",
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleCancel();
      }}
    >
      <div
        style={{
          backgroundColor: "#1a1a2e",
          border: "1px solid #2a2a4a",
          borderRadius: "10px",
          width: "360px",
          padding: "24px",
          position: "relative",
          boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <span style={{ color: "#fff", fontWeight: "600", fontSize: "15px", letterSpacing: "0.3px" }}>
            {isEditing ? "Editar Projeto" : "Salvar Projeto"}
          </span>
          <button
            onClick={handleCancel}
            style={{ background: "none", border: "none", color: "#888", cursor: "pointer", padding: "2px", display: "flex", alignItems: "center" }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Field: Nome do Projeto */}
        <div style={{ marginBottom: "14px" }}>
          <label style={{ display: "flex", alignItems: "center", gap: "4px", color: nameError ? "#ef5350" : "#aaa", fontSize: "12px", marginBottom: "6px", fontWeight: "500" }}>
            Nome do Projeto
            <span style={{ color: "#ef5350", lineHeight: 1 }}>*</span>
          </label>
          <input
            type="text"
            placeholder="Ex: Meu Catálogo de Projetos"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            onBlur={() => setTouched(true)}
            style={{
              ...inputStyle,
              border: `1px solid ${nameError ? "#ef5350" : "#2a2a4a"}`,
            }}
            onFocus={(e) => (e.target.style.borderColor = nameError ? "#ef5350" : "#4caf50")}
            onBlurCapture={(e) => (e.target.style.borderColor = nameError ? "#ef5350" : "#2a2a4a")}
          />
          {nameError && (
            <span style={{ color: "#ef5350", fontSize: "11px", marginTop: "4px", display: "block" }}>
              O nome do projeto é obrigatório.
            </span>
          )}
        </div>

        {/* Field: Ordem De Produção */}
        <div style={{ marginBottom: "14px" }}>
          <label style={{ display: "block", color: "#aaa", fontSize: "12px", marginBottom: "6px", fontWeight: "500" }}>
            Ordem De Produção
          </label>
          <input
            type="text"
            value={productionOrder}
            onChange={(e) => setProductionOrder(e.target.value)}
            style={inputStyle}
            onFocus={(e) => (e.target.style.borderColor = "#4caf50")}
            onBlur={(e) => (e.target.style.borderColor = "#2a2a4a")}
          />
        </div>

        {/* Field: Designer Responsável */}
        <div style={{ marginBottom: "14px" }}>
          <label style={{ display: "block", color: "#aaa", fontSize: "12px", marginBottom: "6px", fontWeight: "500" }}>
            Designer Responsável
          </label>
          <input
            type="text"
            value={designer}
            onChange={(e) => setDesigner(e.target.value)}
            style={inputStyle}
            onFocus={(e) => (e.target.style.borderColor = "#4caf50")}
            onBlur={(e) => (e.target.style.borderColor = "#2a2a4a")}
          />
        </div>

        {/* Field: Caminho do Arquivo */}
        <div style={{ marginBottom: "22px" }}>
          <label style={{ display: "block", color: "#aaa", fontSize: "12px", marginBottom: "6px", fontWeight: "500" }}>
            Caminho do Arquivo
          </label>
          <input
            type="text"
            placeholder="Ex: C:\Users\...\projeto.glb"
            value={filePath}
            onChange={(e) => setFilePath(e.target.value)}
            style={inputStyle}
            onFocus={(e) => (e.target.style.borderColor = "#4caf50")}
            onBlur={(e) => (e.target.style.borderColor = "#2a2a4a")}
          />
        </div>

        {/* Buttons */}
        {isEditing ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <button
              onClick={handleEdit}
              style={{
                width: "100%",
                backgroundColor: projectName.trim() ? "#1976d2" : "#1a3a5c",
                border: "none",
                borderRadius: "6px",
                padding: "10px",
                color: projectName.trim() ? "#fff" : "#4a7aa0",
                fontWeight: "600",
                fontSize: "14px",
                cursor: projectName.trim() ? "pointer" : "not-allowed",
                transition: "background-color 0.2s",
              }}
              onMouseEnter={(e) => { if (projectName.trim()) e.currentTarget.style.backgroundColor = "#1565c0"; }}
              onMouseLeave={(e) => { if (projectName.trim()) e.currentTarget.style.backgroundColor = "#1976d2"; }}
            >
              Editar Projeto
            </button>
            <button
              onClick={handleSaveNew}
              style={{
                width: "100%",
                backgroundColor: projectName.trim() ? "#4caf50" : "#2e4d30",
                border: "none",
                borderRadius: "6px",
                padding: "10px",
                color: projectName.trim() ? "#fff" : "#5a7a5c",
                fontWeight: "600",
                fontSize: "14px",
                cursor: projectName.trim() ? "pointer" : "not-allowed",
                transition: "background-color 0.2s",
              }}
              onMouseEnter={(e) => { if (projectName.trim()) e.currentTarget.style.backgroundColor = "#43a047"; }}
              onMouseLeave={(e) => { if (projectName.trim()) e.currentTarget.style.backgroundColor = "#4caf50"; }}
            >
              Salvar como Novo
            </button>
            <button
              onClick={handleCancel}
              style={{
                backgroundColor: "transparent",
                border: "1px solid #2a2a4a",
                borderRadius: "6px",
                padding: "10px 18px",
                color: "#aaa",
                fontWeight: "500",
                fontSize: "14px",
                cursor: "pointer",
                transition: "border-color 0.2s, color 0.2s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#555"; e.currentTarget.style.color = "#fff"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#2a2a4a"; e.currentTarget.style.color = "#aaa"; }}
            >
              Cancelar
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", gap: "10px" }}>
            <button
              onClick={handleSaveNew}
              style={{
                flex: 1,
                backgroundColor: projectName.trim() ? "#4caf50" : "#2e4d30",
                border: "none",
                borderRadius: "6px",
                padding: "10px",
                color: projectName.trim() ? "#fff" : "#5a7a5c",
                fontWeight: "600",
                fontSize: "14px",
                cursor: projectName.trim() ? "pointer" : "not-allowed",
                transition: "background-color 0.2s",
              }}
              onMouseEnter={(e) => { if (projectName.trim()) e.currentTarget.style.backgroundColor = "#43a047"; }}
              onMouseLeave={(e) => { if (projectName.trim()) e.currentTarget.style.backgroundColor = "#4caf50"; }}
            >
              Salvar
            </button>
            <button
              onClick={handleCancel}
              style={{
                backgroundColor: "transparent",
                border: "1px solid #2a2a4a",
                borderRadius: "6px",
                padding: "10px 18px",
                color: "#aaa",
                fontWeight: "500",
                fontSize: "14px",
                cursor: "pointer",
                transition: "border-color 0.2s, color 0.2s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#555"; e.currentTarget.style.color = "#fff"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#2a2a4a"; e.currentTarget.style.color = "#aaa"; }}
            >
              Cancelar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
